/**
 * Search grid page size.
 *
 * A full page is a multiple of the rendered column count so the last row is
 * complete, and it grows when the viewport can show more rows. The size is
 * kept in module state (not the URL) so filter changes reuse the last
 * measurement. Server render cannot see the window and stays on the default.
 */

export const SEARCH_PAGE_SIZE_DEFAULT = 24;
export const SEARCH_PAGE_SIZE_MAX = 100;
export const SEARCH_PAGE_SIZE_MIN_LISTINGS = 24;
export const SEARCH_PAGINATION_BLOCK_FALLBACK_PX = 64;

let clientSearchPageSize = null;

const parsePx = value => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/**
 * @param {string} value Computed `grid-template-columns`.
 * @returns {number}
 */
export const columnCountFromGridTemplateColumns = value => {
  if (typeof value !== 'string') {
    return 0;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed === 'none') {
    return 0;
  }
  return trimmed.split(/\s+/).length;
};

/**
 * How many card rows fit between the grid's top and the bottom of the viewport,
 * after leaving room for the pagination links.
 *
 * @param {Object} metrics
 * @param {number} metrics.viewportHeightPx
 * @param {number} metrics.gridOffsetTopPx Document offset of the grid (rect.top + scrollY).
 * @param {number} metrics.rowHeightPx
 * @param {number} metrics.rowGapPx
 * @param {number} metrics.paginationBlockPx
 * @returns {number}
 */
export const rowsThatFitInViewport = ({
  viewportHeightPx,
  gridOffsetTopPx,
  rowHeightPx,
  rowGapPx,
  paginationBlockPx,
}) => {
  const rowHeight = Number(rowHeightPx);
  if (!(rowHeight > 0)) {
    return 0;
  }
  const gap = Number.isFinite(rowGapPx) && rowGapPx > 0 ? rowGapPx : 0;
  const pagination =
    Number.isFinite(paginationBlockPx) && paginationBlockPx > 0 ? paginationBlockPx : 0;
  const available = viewportHeightPx - gridOffsetTopPx - pagination;
  if (!(available > 0)) {
    return 0;
  }
  return Math.max(0, Math.floor((available + gap) / (rowHeight + gap)));
};

/**
 * Page size for one search request. Never below a full-row rounding of
 * `minListings`, and never above the largest multiple of the column count
 * that Sharetribe will accept.
 *
 * @param {Object} args
 * @param {number} args.columnCount
 * @param {number} [args.rowsThatFit]
 * @param {number} [args.minListings]
 * @param {number} [args.maxListings]
 * @returns {number}
 */
export const searchPageSizeForGrid = ({
  columnCount,
  rowsThatFit = 0,
  minListings = SEARCH_PAGE_SIZE_MIN_LISTINGS,
  maxListings = SEARCH_PAGE_SIZE_MAX,
} = {}) => {
  const columns = Math.max(1, Math.floor(Number(columnCount)) || 1);
  const minRows = Math.max(1, Math.ceil(minListings / columns));
  const fitted = Math.max(0, Math.floor(Number(rowsThatFit)) || 0);
  const maxRows = Math.max(1, Math.floor(maxListings / columns));
  const rows = Math.min(maxRows, Math.max(minRows, fitted));
  return columns * rows;
};

/**
 * @param {Element | { getBoundingClientRect: Function, querySelector: Function }} gridEl
 * @param {Element | null} paginationEl
 * @param {Window | { innerHeight: number, scrollY: number, getComputedStyle: Function }} view
 * @returns {number | null}
 */
export const pageSizeFromGridElement = (gridEl, paginationEl, view) => {
  if (!gridEl || !view) {
    return null;
  }
  const rect = gridEl.getBoundingClientRect();
  if (!(rect.width > 0)) {
    return null;
  }
  const style = view.getComputedStyle(gridEl);
  const columnCount = columnCountFromGridTemplateColumns(style.gridTemplateColumns);
  if (columnCount < 1) {
    return null;
  }
  const firstItem =
    gridEl.firstElementChild ||
    (typeof gridEl.querySelector === 'function' ? gridEl.querySelector('li') : null);
  const rowHeight = firstItem ? firstItem.getBoundingClientRect().height : 0;
  if (!(rowHeight > 0)) {
    return null;
  }
  const paginationHeight = paginationEl ? paginationEl.getBoundingClientRect().height : 0;
  const rowsThatFit = rowsThatFitInViewport({
    viewportHeightPx: view.innerHeight,
    gridOffsetTopPx: rect.top + (view.scrollY || 0),
    rowHeightPx: rowHeight,
    rowGapPx: parsePx(style.rowGap) || parsePx(style.gap),
    paginationBlockPx:
      paginationHeight > 0 ? paginationHeight : SEARCH_PAGINATION_BLOCK_FALLBACK_PX,
  });
  return searchPageSizeForGrid({ columnCount, rowsThatFit });
};

/**
 * @param {number} size
 */
export const setClientSearchPageSize = size => {
  const next = Math.floor(Number(size));
  clientSearchPageSize = next > 0 ? next : null;
};

export const resetClientSearchPageSize = () => {
  clientSearchPageSize = null;
};

/**
 * @param {boolean} [hasWindow] Pass false for the server render path.
 * @returns {number}
 */
export const resolveSearchPageSize = (hasWindow = typeof window !== 'undefined') => {
  if (!hasWindow) {
    return SEARCH_PAGE_SIZE_DEFAULT;
  }
  return clientSearchPageSize || SEARCH_PAGE_SIZE_DEFAULT;
};

/**
 * Whether the grid measurement should start another listings query.
 * Skips a size already on screen, already in flight, or already queued.
 *
 * @param {number} nextSize
 * @param {Object} state
 * @param {number | string | null} state.activePerPage
 * @param {number | string | null} state.inFlightPerPage
 * @param {boolean} state.searchInProgress
 * @param {number | null} state.pendingPageSize
 * @returns {boolean}
 */
export const shouldRequestSearchPageSize = (nextSize, state) => {
  if (!nextSize) {
    return false;
  }
  if (nextSize === Number(state.activePerPage)) {
    return false;
  }
  if (state.searchInProgress && nextSize === Number(state.inFlightPerPage)) {
    return false;
  }
  if (nextSize === state.pendingPageSize) {
    return false;
  }
  return true;
};
